import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Plus, Home, Loader2, Layers, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import type { Project, Dwelling } from "@/api/types";
import { StatusBadge } from "@/components/StatusBadge";
import { Modal, Field, inputCls } from "@/components/Modal";

export default function ProjectView() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", levels_count: 1 });

  const { data: project, isLoading } = useQuery<Project>({ queryKey: ["project", id], queryFn: () => api.getProject(id) });
  const { data: dwellings = [] } = useQuery<Dwelling[]>({ queryKey: ["dwellings", id], queryFn: () => api.listDwellings(id) });

  const createMut = useMutation({
    mutationFn: () => api.createDwelling(id, { name: form.name, levels_count: Number(form.levels_count) }),
    onSuccess: (d) => {
      toast.success("Logement créé");
      qc.invalidateQueries({ queryKey: ["dwellings", id] });
      setOpen(false);
      navigate(`/dwellings/${d._id}`);
    },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading || !project) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-8">
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Tableau de bord
      </button>

      <div data-testid="project-header" className="rounded-xl border border-border/60 bg-card p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{project.name}</h1>
              <StatusBadge status={project.status} testId="project-status-badge" />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{project.description || "Aucune description"}</p>
            <div className="mt-3 flex flex-wrap items-center gap-4 uppercase-label text-muted-foreground">
              <span className="flex items-center gap-1.5"><Home className="h-3.5 w-3.5" />{project.property_type}</span>
              {project.address?.label && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{project.address.label}, {project.address.city}</span>}
            </div>
          </div>
          <button
            data-testid="create-dwelling-button"
            onClick={() => setOpen(true)}
            className="flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> Nouveau logement
          </button>
        </div>
      </div>

      <div>
        <h2 className="mb-4 text-xl font-semibold">Logements ({dwellings.length})</h2>
        {dwellings.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
            Aucun logement. Ajoutez un logement pour commencer la revue des pièces.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {dwellings.map((d) => (
              <button
                key={d._id}
                data-testid={`dwelling-card-${d._id}`}
                onClick={() => navigate(`/dwellings/${d._id}`)}
                className="group rounded-xl border border-border/60 bg-card p-5 text-left transition-all hover:border-primary/40 hover:bg-[#1A2234]"
              >
                <div className="flex items-start justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 border border-primary/20">
                    <Layers className="h-5 w-5 text-primary" />
                  </div>
                  <StatusBadge status={d.validation_status} />
                </div>
                <h3 className="mt-4 text-lg font-semibold group-hover:text-primary">{d.name}</h3>
                <p className="mt-1 uppercase-label text-muted-foreground">{d.levels_count} niveau(x)</p>
              </button>
            ))}
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Nouveau logement" testId="create-dwelling-modal">
        <form onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }} className="space-y-4">
          <Field label="Nom du logement">
            <input data-testid="dwelling-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Main dwelling" />
          </Field>
          <Field label="Nombre de niveaux">
            <input data-testid="dwelling-levels-input" type="number" min={1} value={form.levels_count} onChange={(e) => setForm({ ...form, levels_count: Number(e.target.value) })} className={inputCls} />
          </Field>
          <button data-testid="dwelling-submit-button" type="submit" disabled={createMut.isPending} className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {createMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Créer le logement
          </button>
        </form>
      </Modal>
    </div>
  );
}
